import { useEffect, useState } from 'react';

import { useApplyAnnouncementHeartBatch, useGetAnnouncementHeartCount } from '@kc-console/context';

interface AnnouncementHeart {
  /** 하트를 1개 추가합니다. 서버 전송은 배치 훅이 모아서 처리합니다. */
  addHeart: () => void;
  /** 화면에 표시할 하트 수 */
  count: number;
  /** 카운트를 표시해도 되는 상태인지 여부 (최초 조회 전에는 숫자를 감춥니다) */
  isCountReady: boolean;
  /** 아직 서버로 보내지 않고 모아둔 하트 수 */
  pendingCount: number;
}

/**
 * 새소식 하트의 카운트 조회와 클릭 반영을 화면에 필요한 형태로 묶어줍니다.
 * @description
 * - 표시 카운트는 `서버 값`과 `최초 서버 값 + 내가 누른 수` 중 큰 쪽을 사용합니다.
 *   전송이 늦거나 다른 사용자의 하트가 섞여도 숫자가 뒤로 줄지 않습니다.
 * - 전송에 실패한 분량은 낙관적 카운트에서 되돌려 서버 값과 어긋나지 않게 합니다.
 * - 연타를 모아 보내는 것과 언마운트 시 전송은 useApplyAnnouncementHeartBatch 가 담당합니다.
 */
const useAnnouncementHeart = (announcementId: string): AnnouncementHeart => {
  const { data, isSuccess } = useGetAnnouncementHeartCount(announcementId);
  const [addedCount, setAddedCount] = useState(0);
  const [baseCount, setBaseCount] = useState<number | null>(null);

  const { addHeart: applyHeart, pendingCount } = useApplyAnnouncementHeartBatch(announcementId, {
    onError: (_error, delta) => {
      setAddedCount(previous => Math.max(0, previous - delta));
    },
  });

  useEffect(() => {
    if (isSuccess && baseCount === null) setBaseCount(data.heartCount);
  }, [baseCount, data, isSuccess]);

  const addHeart = () => {
    setAddedCount(previous => previous + 1);
    applyHeart();
  };

  return {
    count: Math.max(data?.heartCount ?? 0, (baseCount ?? 0) + addedCount),
    isCountReady: isSuccess || addedCount > 0,
    pendingCount,
    addHeart,
  };
};

export default useAnnouncementHeart;
